import React, { useState, useEffect } from 'react';
import useBusinessDate from './hooks/useBusinessDate';
import { apiGet } from './apiUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Label } from './components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Alert, AlertDescription } from './components/ui/alert';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './components/ui/popover';
import { Calendar as CalendarComponent } from './components/ui/calendar';
import { 
  Search, 
  Calendar, 
  PackageOpen, 
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from './lib/utils';
import { useNavigation } from './contexts/NavigationContext';

function StockReceived() {
  const { navigate } = useNavigation();
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStore, setSelectedStore] = useState('ALL');
  const [stores, setStores] = useState(['ALL']);
  const [selectedDate, setSelectedDate] = useState('');
  const [calendarDate, setCalendarDate] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const businessDate = useBusinessDate();

  // Initialize selected date with business date
  useEffect(() => {
    if (businessDate && !selectedDate) {
      setSelectedDate(businessDate);
      setCalendarDate(new Date(businessDate));
    }
  }, [businessDate, selectedDate]);

  // Fetch stock received data
  useEffect(() => {
    if (selectedDate) {
      fetchStockReceivedData();
    }
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedStore]);

  const fetchStores = async () => {
    try {
      const response = await apiGet('/api/stores');

      if (response.ok) {
        const data = await response.json();
        const storeNames = data.map(store => {
          if (store.retailer_code !== '0000000' && store.retailer_code !== store.shop_name) {
            return `${store.shop_name} (${store.retailer_code})`;
          }
          return store.shop_name;
        });
        setStores(['ALL', ...storeNames]);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchStockReceivedData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching stock received data...');
      console.log('Date:', selectedDate);
      
      const response = await apiGet(`/api/stock-received?startDate=${selectedDate}&endDate=${selectedDate}&storeFilter=${selectedStore}`);
      const data = await response.json();
      console.log('Data received:', data);
      setStockData(data.records || []);
    } catch (err) {
      console.error('Error fetching stock received data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter data based on search term only (backend handles store filtering)
  const filteredData = stockData.filter(record => {
    const matchesSearch = record.brandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.sizeCode.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });



  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p>Loading stock received data...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="space-y-2 text-left">
          <h1 className="text-4xl font-bold text-left">Stock Received</h1>
          <p className="text-base text-muted-foreground text-left">View and track received stock records by date and store</p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Error: {error}</span>
              <Button variant="outline" size="sm" onClick={fetchStockReceivedData}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Filters Card */}
        {!error && (
          <Card>
            <CardHeader>
              <CardTitle className="text-left">Filters</CardTitle>
              <CardDescription className="text-left">Search and filter stock received records</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div className="space-y-2">
                  <Label htmlFor="search" className="text-left">Search</Label>
                  <div className="relative">
                    <Input
                      id="search"
                      type="text"
                      placeholder="Brand name or size code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pr-10"
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  </div>
                </div>

                {/* Store Selection */}
                <div className="space-y-2">
                  <Label htmlFor="store-select" className="text-left">Store</Label>
                  <Select value={selectedStore} onValueChange={setSelectedStore}>
                    <SelectTrigger id="store-select">
                      <SelectValue placeholder="Select a store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map(store => (
                        <SelectItem key={store} value={store}>
                          {store}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Picker */}
                <div className="space-y-2">
                  <Label className="text-left flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Business Date
                  </Label>
                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !selectedDate && "text-muted-foreground"
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {selectedDate ? formatDate(selectedDate) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={calendarDate}
                        onSelect={(date) => {
                          if (date) {
                            const formattedDate = format(date, 'yyyy-MM-dd');
                            setSelectedDate(formattedDate);
                            setCalendarDate(date);
                            setIsCalendarOpen(false);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground text-left">
                    Current: {formatDate(businessDate)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Table Card */}
        {!error && (
          <Card>
            {filteredData.length === 0 ? (
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="mx-auto h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <PackageOpen className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-2">No Stock Received</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  No stock received records found for {formatDate(selectedDate)}
                </p>
                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => navigate('uploadInvoice')}>
                    Upload Invoice
                  </Button>
                </div>
              </CardContent>
            ) : (
              <>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-left">
                    <span className="text-left">Stock Received Records</span>
                    <Badge variant="secondary">{filteredData.length} records</Badge>
                  </CardTitle>
                  <CardDescription className="text-left">
                    Showing received stock for {formatDate(selectedDate)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead rowSpan={2} className="w-[60px]">S.No</TableHead>
                          <TableHead rowSpan={2}>Brand Name</TableHead>
                          <TableHead rowSpan={2}>Size Code</TableHead>
                          <TableHead colSpan={3} className="text-center">Received Stock</TableHead>
                          <TableHead rowSpan={2} className="text-right">Total Received</TableHead>
                        </TableRow>
                        <TableRow>
                          <TableHead className="text-right">Invoice</TableHead>
                          <TableHead className="text-right">Shift In</TableHead>
                          <TableHead className="text-right">Shift Out</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredData.map((record, index) => (
                          <TableRow key={`${record.brandNumber}-${record.sizeCode}-${index}`}>
                            <TableCell className="font-medium">{index + 1}</TableCell>
                            <TableCell>
                              <div className="space-y-1 text-left">
                                <p className="font-medium">{record.brandName}</p>
                                <p className="text-xs text-muted-foreground">#{record.brandNumber} ({record.packType || '-'})</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-left">
                              <div className="space-y-1">
                                <p className="font-medium">{record.sizeCode}</p>
                                <p className="text-xs text-muted-foreground">({record.size}ml)</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={cn(
                                "font-mono font-semibold text-base",
                                record.invoiceQuantity > 0 ? 'text-blue-600' : 
                                record.invoiceQuantity < 0 ? 'text-red-600' : 
                                'text-muted-foreground'
                              )}>
                                {record.invoiceQuantity || 0}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={cn(
                                "font-mono font-semibold text-base",
                                record.shiftIn > 0 ? 'text-green-600' : 'text-muted-foreground'
                              )}>
                                {record.shiftIn || 0}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={cn(
                                "font-mono font-semibold text-base",
                                record.shiftOut < 0 ? 'text-red-600' : 'text-muted-foreground'
                              )}>
                                {record.shiftOut || 0}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={cn(
                                "font-mono font-bold text-base",
                                record.totalReceived > 0 ? 'text-green-600' : 
                                record.totalReceived < 0 ? 'text-red-600' : 
                                'text-muted-foreground'
                              )}>
                                {record.totalReceived}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        )}
      </main>
  );
}

export default StockReceived;
